import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";

const NAV_LINKS = [
  { label: "业务板块", href: "#business" },
  { label: "产品矩阵", href: "#products" },
  { label: "核心技术", href: "#technology" },
  { label: "发展历程", href: "#timeline" },
  { label: "全球布局", href: "#global" },
  { label: "联系我们", href: "#contact" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[oklch(0.10_0.05_252/0.92)] backdrop-blur-xl border-b border-white/5 shadow-lg shadow-black/20"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3 group"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-cyan-400 flex items-center justify-center shadow-md shadow-primary/30">
              <span className="text-white font-black text-sm">K</span>
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-white font-bold text-base tracking-wide group-hover:text-primary transition-colors">
                昆仑万维
              </span>
              <span className="text-white/40 text-[10px] tracking-widest uppercase">Kunlun Tech</span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className="px-3 py-1.5 text-sm text-white/70 hover:text-white rounded-md hover:bg-white/5 transition-all duration-200"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="http://www.kunlun.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-all duration-200 shadow-md shadow-primary/25"
            >
              官方网站 <ChevronRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden p-2 text-white/70 hover:text-white"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed top-16 left-0 right-0 z-40 bg-[oklch(0.10_0.05_252/0.98)] backdrop-blur-xl border-b border-white/5 md:hidden"
          >
            <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  className="text-left px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 rounded-lg text-sm transition-all"
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function Footer() {
  return (
    <footer className="relative border-t border-white/5 bg-[oklch(0.09_0.045_254)]">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-cyan-400 flex items-center justify-center">
                <span className="text-white font-black">K</span>
              </div>
              <div>
                <div className="text-white font-bold">昆仑万维</div>
                <div className="text-white/40 text-xs">Kunlun Tech · 300418</div>
              </div>
            </div>
            <p className="text-white/40 text-sm leading-relaxed">
              实现通用人工智能，让每个人更好地塑造和表达自我。
            </p>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white/60 text-xs uppercase tracking-widest mb-4 font-semibold">核心产品</h4>
            <ul className="space-y-2">
              {["天工超级智能体", "SkyReels AI 视频", "Mureka AI 音乐", "Linky AI 社交", "Matrix-Game 世界模型"].map(
                (item) => (
                  <li key={item}>
                    <span className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-default">
                      {item}
                    </span>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white/60 text-xs uppercase tracking-widest mb-4 font-semibold">公司</h4>
            <ul className="space-y-2">
              {["关于我们", "发展历程", "全球布局", "投资者关系", "加入我们"].map((item) => (
                <li key={item}>
                  <span className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-default">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white/60 text-xs uppercase tracking-widest mb-4 font-semibold">联系方式</h4>
            <ul className="space-y-3">
              <li className="text-white/50 text-sm">
                <span className="block text-white/30 text-xs mb-0.5">官方网站</span>
                <a
                  href="http://www.kunlun.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors"
                >
                  www.kunlun.com
                </a>
              </li>
              <li className="text-white/50 text-sm">
                <span className="block text-white/30 text-xs mb-0.5">股票代码</span>
                深交所 · 300418
              </li>
              <li className="text-white/50 text-sm">
                <span className="block text-white/30 text-xs mb-0.5">天工 AI</span>
                <a
                  href="https://tiangong.cn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors"
                >
                  tiangong.cn
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/25 text-sm">
            © 2026 昆仑万维科技股份有限公司 · Kunlun Tech Co., Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {["隐私政策", "服务条款", "免责声明"].map((item) => (
              <span
                key={item}
                className="text-white/25 text-xs hover:text-white/50 transition-colors cursor-default"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
